import { useContext, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { GridCellParams } from '@mui/x-data-grid-pro';

import type { SidePanelContextColumnsPropsType } from '../../SidePanelModelsProvider';
import { SidePanelProviderContext } from '../../SidePanelModelsProvider';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { tableColumnTypes } from '../../../../../../muiTheme/MuiDataGrid';
import type { TableType, TableColumnType } from '../../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useTableManipulation } from '../../../../GridDndEditor/Block/Table/useTableManipulation';
import { useTableColumnManipulation } from './useTableColumnManipulation';
import { TableSettingsTypes } from '../types';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo.ts';
import { useTableTotals } from '../../../../GridDndEditor/Block/Table/useTableTotals';
import { clearRowDiscountsIfNoPriceColumn } from 'components/editor/GridDndEditor/Block/Table/tableUtils/clearRowDiscounts';
import type { CrmRowRefState } from 'components/editor/GridDndEditor/Block/Table/tableUtils/crmOrphanFamilies';
import {
  dropGeneratedRowsForSources,
  resolveOrphanedSourceRowIds,
} from 'components/editor/GridDndEditor/Block/Table/tableUtils/crmOrphanFamilies';
import { classifyRowStateAfterColumnRemoval } from 'components/editor/GridDndEditor/Block/Table/tableUtils/deriveCrmRows';

const NUMERIC_COLUMN_TYPES = [ColumnTypeIdentifier.SUBTOTAL, ColumnTypeIdentifier.PRICE, ColumnTypeIdentifier.MULTIPLIER];

const updateColumns = (
  columns: TableType['columns'],
  columnData: Pick<TableColumnType, 'field'>,
  newColumnType: ColumnTypeIdentifier,
  isNumericColumn: boolean
): TableColumnType[] => {
  const modifiedColumns = columns.map((column) => {
    const newColumnObject = {
      ...column,
      headerName: newColumnType,
      columnType: newColumnType,
      type: isNumericColumn ? 'number' : 'string',
    } as TableColumnType;
    return column.field !== columnData?.field ? column : newColumnObject;
  });

  return applySubtotalRules(modifiedColumns, newColumnType, isNumericColumn);
};

function applySubtotalRules(
  modifiedColumns: TableColumnType[],
  newColumnType: ColumnTypeIdentifier,
  isNumericColumn: boolean
): TableColumnType[] {
  if (
    newColumnType === ColumnTypeIdentifier.SUBTOTAL &&
    modifiedColumns[modifiedColumns.length - 1].columnType !== ColumnTypeIdentifier.SUBTOTAL
  ) {
    const filteredColumn = modifiedColumns.filter((column) => column.columnType !== ColumnTypeIdentifier.SUBTOTAL);
    const oldColumnForConversion = modifiedColumns.find((column) => column.columnType === ColumnTypeIdentifier.SUBTOTAL);

    if (!oldColumnForConversion) {
      return modifiedColumns;
    }
    const subtotalColumn: TableColumnType = {
      ...oldColumnForConversion,
      headerName: newColumnType,
      columnType: newColumnType,
      field: oldColumnForConversion.field,
      type: isNumericColumn ? 'number' : 'string',
    };

    return [...filteredColumn, subtotalColumn];
  }

  return modifiedColumns;
}

const updateRows = (
  rows: TableType['rows'],
  columns: TableColumnType[],
  selectedColumn: string,
  newColumnType: ColumnTypeIdentifier,
  isNumericColumn: boolean,
  subtotalField?: string
) => {
  // A PRICE column takes over subtotal calculation: any manual override (and the
  // stale subtotal value) must be cleared so the engine/legacy calculation wins.
  const isChangingToPrice = newColumnType === ColumnTypeIdentifier.PRICE;
  const stateByRowId = new Map<string, CrmRowRefState>();

  const updatedRows = rows.map((row) => {
    if (row.rowType !== RowTypesIdentifier.BODY) return row;
    const isNumeric = !isNaN(Number(row[selectedColumn]));
    const coerced = isNumeric === isNumericColumn ? row : { ...row, [selectedColumn]: '' };

    const { row: baseRow, state } = classifyRowStateAfterColumnRemoval(coerced, selectedColumn, columns);
    stateByRowId.set(row.id, state);

    return isChangingToPrice ? { ...baseRow, manualSubtotalOverride: null, ...(subtotalField && { [subtotalField]: '' }) } : baseRow;
  });

  const orphanedSourceRowIds = resolveOrphanedSourceRowIds(updatedRows, (row) => stateByRowId.get(row.id) ?? 'unbound');
  return dropGeneratedRowsForSources(updatedRows, orphanedSourceRowIds);
};

const findSubtotalField = (columns: TableType['columns']): string | undefined =>
  columns.find((column) => column.columnType === ColumnTypeIdentifier.SUBTOTAL)?.field;

export const useTableColumnTypeChange = () => {
  const { toggledTableSettingsPanel } = useContext(SidePanelProviderContext) as SidePanelContextColumnsPropsType;
  const { selectedBlockIdByWrapper, selectedSectionId } = useContext(SelectionContext);
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const getTableTotals = useTableTotals();
  const { getTableData } = useTableManipulation();
  const { setColumnFocus } = useTableColumnManipulation();

  const [columnType, setColumnType] = useState<string>('');

  const activeTableSettingsPanel = useSelector((state: RootState) => state.blockStyleSettings.activeSidePanel);

  const getActiveColumnType = useCallback(() => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper || !selectedSectionId) return '';

    const { field } = toggledTableSettingsPanel.tableApi.selectedModel || {};
    if (!field) return '';
    const tableData = getTableData(selectedBlockIdByWrapper, selectedSectionId);
    if (!tableData) return '';

    const column = tableData.columns.find((col) => col.field === field);
    if (!column) return '';

    const activeColumnKey = column.columnType?.toUpperCase() as string;
    return tableColumnTypes[activeColumnKey] || '';
  }, [toggledTableSettingsPanel, selectedBlockIdByWrapper, selectedSectionId, getTableData]);

  useEffect(() => {
    const activeColumnType = getActiveColumnType();
    if (activeColumnType) setColumnType(activeColumnType);
  }, [getActiveColumnType]);

  const handleColumnTypeChange = useCallback(
    async (event: SelectChangeEvent) => {
      const isToggledPanelIncludeIds = !toggledTableSettingsPanel || !selectedBlockIdByWrapper || !selectedSectionId;
      if (activeTableSettingsPanel !== TableSettingsTypes.TABLE_COLUMNS || isToggledPanelIncludeIds) return;

      const { field: selectedColumn } = toggledTableSettingsPanel.tableApi.selectedModel as GridCellParams;
      const newColumnType = event.target.value as ColumnTypeIdentifier;
      const isNumericColumn = NUMERIC_COLUMN_TYPES.includes(newColumnType);

      const tableData = getTableData(selectedBlockIdByWrapper, selectedSectionId) as TableType;
      const { colDef: columnData } = toggledTableSettingsPanel.tableApi.selectedModel as GridCellParams;

      const updatedColumns = updateColumns(tableData.columns, columnData, newColumnType, isNumericColumn);
      // Retyping the last PRICE column away also drops the discounts' basis.
      const updatedRows = clearRowDiscountsIfNoPriceColumn(
        updateRows(tableData.rows, tableData.columns, selectedColumn, newColumnType, isNumericColumn, findSubtotalField(tableData.columns)),
        updatedColumns
      );
      const { rows, totalRows, metadata } = getTableTotals(updatedRows, updatedColumns, tableData);
      const newTableData: TableType = {
        ...tableData,
        columns: updatedColumns,
        rows,
      };
      handleTableContentChanged(selectedBlockIdByWrapper, selectedSectionId, tableData, {
        ...newTableData,
        totalRows,
        metadata,
      });

      setColumnType(newColumnType);
      setColumnFocus(selectedColumn);
    },
    [
      toggledTableSettingsPanel,
      selectedBlockIdByWrapper,
      selectedSectionId,
      activeTableSettingsPanel,
      getTableData,
      handleTableContentChanged,
    ]
  );

  const handleColumnTypeChangeById = useCallback(
    async (columnField: string, newColumnType: ColumnTypeIdentifier) => {
      if (!selectedBlockIdByWrapper || !selectedSectionId) return;

      const tableData = getTableData(selectedBlockIdByWrapper, selectedSectionId) as TableType;
      const columnData = tableData.columns.find((c) => c.field === columnField);
      if (!columnData) return;

      const isNumeric = NUMERIC_COLUMN_TYPES.includes(newColumnType);

      const updatedColumns = updateColumns(tableData.columns, columnData, newColumnType, isNumeric);
      const updatedRows = updateRows(
        tableData.rows,
        tableData.columns,
        columnField,
        newColumnType,
        isNumeric,
        findSubtotalField(tableData.columns)
      );
      const { rows, totalRows, metadata } = getTableTotals(updatedRows, updatedColumns, tableData);
      const newTableData: TableType = {
        ...tableData,
        columns: updatedColumns,
        rows,
      };
      handleTableContentChanged(selectedBlockIdByWrapper, selectedSectionId, tableData, {
        ...newTableData,
        totalRows,
        metadata,
      });

      setColumnType(newColumnType);
      setColumnFocus(columnField);
    },
    [selectedBlockIdByWrapper, selectedSectionId, getTableData, handleTableContentChanged, setColumnType, setColumnFocus]
  );

  return {
    columnType,
    handleColumnTypeChange,
    handleColumnTypeChangeById,
  };
};
